// MAJLIS-9 (A-3, TS-3, EM-18): this seat publishes ONE microphone track to LiveKit, in live-audio AND screen-only
// rooms (the server's hidden ingest always subscribes). MAJLIS-62: the same connection receives the other seats'
// voices when the server lets it subscribe; they are handed to `hearing` (media/hearing.ts) and cleared with the
// connection.
//
// Lifecycle (consultant ruling 2026-09-19, MAJLIS-9 answer 5):
// - joined with a grant: connect(url, token), then publish the mic track once the microphone is open (and, since
//   MAJLIS-61, while the seat is unmuted and LiveKit grants canPublish: see the safety floor below);
// - mute mutes the published track (the server is told muted, and since MAJLIS-61 takes it down); unmute resumes the
//   same track, or publishes the same track again when the server took it down;
// - leave, and the page going away, DISCONNECT and nothing else (MAJLIS-53: one round trip, not two - see stop());
// - resumed carries a FRESH grant: if the LiveKit connection is down, connect again with the NEW token and publish
//   again; if it is up (or still connecting), keep it. A grant is used for at most one connect, never reused;
// - no grant (media disabled on the server) or no microphone (denied, no device): publish nothing.
//
// Asking again (consultant ruling 2026-09-19, M1): when LiveKit gives the connection up (onDisconnected, never our own
// stop) while the room socket is still live, no resumed will bring a grant, so the publisher asks for one itself
// (requestGrant: the client's media_grant) at once. The reply's fresh grant connects and the same track is published
// again; null means media is off on this server: nothing more. A refusal (media_rate_limited) or a failed connect with
// a fresh grant asks again later on the injected clock (MEDIA_GRANT_BACKOFF_MS), each attempt with its own grant; a
// successful connect resets the backoff. With the socket down nothing is sent: the resume brings a fresh grant.
// A request with no reply by MEDIA_GRANT_REPLY_TIMEOUT_MS counts as a refusal; a reply after that is not used.
// Each request carries a fresh ref (MAJLIS-22): a reply or refusal is used only when the ref the server echoed is the
// outstanding request's; any other (a late reply, even while a newer request is out, or one with no ref) is ignored.
// The token and the grant are never logged; the room code never reaches this module (it is not in the grant).
//
// The safety floor (MAJLIS-61, client half; measured in MAJLIS-48 on Chromium + livekit-client 2.22.3, NOT iOS): the
// server enforces OUR mute through LiveKit. While this seat is muted it revokes the seat's `canPublish`, and LiveKit
// then UNPUBLISHES the microphone track server-side (onUnpublished); a muted seat is also minted canPublish=false at
// join, resume and media_grant. Restoring `canPublish` does NOT bring the audio back: this module publishes again.
// - Publish only when ALL hold: the connection is up, there is a live track, OUR protocol says unmuted, and LiveKit
//   says `canPublish` NOW (MediaRoom.canPublish(), read at the moment of the call). Whichever of the unmute and the
//   permission restore arrives last triggers the one re-publish; the other finds a precondition missing.
// - Never publish while `canPublish` is false: livekit-client refuses that locally in ~3 ms, but a publish that races
//   a revoke can hang ~10 s before it rejects. So a seat muted at connect (minted canPublish=false) publishes nothing.
// - The SAME MediaStreamTrack is published again (mic.ts owns it and LiveKit never stops it); an ended track is never
//   published (logged), and this module never opens a second capture.
// - A publish still pending when the seat is muted again: the last word wins. If it lands it is muted at once (and the
//   server's revoke takes it down); if it fails, nothing is retried until the unmute or the permission changes.
import type { Clock, TimerId } from "../clock";
import { MEDIA_GRANT_BACKOFF_MS, MEDIA_GRANT_REPLY_TIMEOUT_MS } from "../constants";
import type { LiveKitGrant } from "../protocol/types";
import type { RemoteAudioSink } from "./hearing";
import type { VoiceTrackEvent, VoiceTrackLink } from "./voiceTrack";

/** One LiveKit room connection. The real one wraps livekit-client (media/livekitRoom.ts); tests use a fake. */
export interface MediaRoom {
  connect(url: string, token: string): Promise<void>;
  /** LiveKit's CURRENT publish permission for this participant (false before the join response carries one). */
  canPublish(): boolean;
  publish(track: MediaStreamTrack, source: "microphone"): Promise<void>;
  mute(): Promise<void>;
  unmute(): Promise<void>;
  unpublish(): Promise<void>;
  disconnect(): Promise<void>;
  /** MAJLIS-140 (P-39): subscribe to, or drop, a passive seat's voice track (absent: the fakes that have none). */
  setVoiceSubscribed?(want: boolean): void;
}

/** What a MediaRoom reports back. */
export interface MediaRoomEvents {
  /** LiveKit gave the connection up (not on our own disconnect()). */
  onDisconnected: () => void;
  /** The server took our microphone track down (the permission was revoked); never fired for our own unpublish(). */
  onUnpublished?: () => void;
  /** The publish permission changed. It carries NOTHING on purpose: LiveKit's event passes the PREVIOUS permission,
   * so the current one is read from MediaRoom.canPublish(). */
  onPermissionsChanged?: () => void;
  /** MAJLIS-62: LiveKit subscribed this seat to another seat's voice (keyed by the publication's track sid). */
  onRemoteAudio?: (key: string, track: MediaStreamTrack) => void;
  /** MAJLIS-62: that subscription ended. */
  onRemoteAudioGone?: (key: string) => void;
  /** MAJLIS-140 (P-39): the passive seat's voice track was published, subscribed, unsubscribed, failed or went. */
  onVoiceTrack?: (e: VoiceTrackEvent) => void;
}

/** A fresh MediaRoom per connection. */
export type MediaRoomFactory = (events: MediaRoomEvents) => MediaRoom;

export type MediaState = "down" | "connecting" | "up";

export interface PublisherOptions {
  factory: MediaRoomFactory;
  /** Diagnostics; receives no token, no grant, no room code. */
  log?: (message: string, body?: unknown) => void;
  /** Ask the room socket for a fresh grant (media_grant); returns the request's ref, or null when it was not sent
   * (the socket is not live: the resume path brings one). Without it, or without a clock, a lost connection waits
   * for the next resumed. */
  requestGrant?: () => string | null;
  /** The injected clock for the backoff between requests (Q-1). */
  clock?: Clock;
  /** EM-25: livekit-client's mute()/unmute() also writes MediaStreamTrack.enabled, and the microphone gate owns
   * that flag. Called after each of those settles so the gate can write the flag it computed. */
  reassertMic?: () => void;
  /** MAJLIS-62: where the other seats' voices go (media/hearing.ts). Only the current connection's reach it, and a
   * connection that ends (lost, stopped, replaced) takes its voices with it. */
  hearing?: RemoteAudioSink;
  /** MAJLIS-140 (D-59, P-39): a passive seat's voice track - told what the current connection says about it, and given
   * that connection to subscribe or unsubscribe through. */
  voiceTrack?: VoiceTrackLink;
}

/** An error as text, with every secret it might carry (a token, a URL's access_token) removed. */
export function redact(e: unknown, secrets: readonly string[]): string {
  const name = (e as { name?: unknown })?.name;
  const message = (e as { message?: unknown })?.message;
  let text = `${typeof name === "string" ? name : "Error"}: ${typeof message === "string" ? message : String(e)}`;
  for (const s of secrets) if (s) text = text.split(s).join("<redacted>");
  return text.replace(/access_token=[^&\s"']*/g, "access_token=<redacted>");
}

export class Publisher {
  state: MediaState = "down";
  /** Whether the mic track is published on the current connection. */
  published = false;

  private room: MediaRoom | null = null;
  private fresh: LiveKitGrant | null = null;
  private track: MediaStreamTrack | null = null;
  private publishing = false;
  private muted = false;
  private roomMuted = false;
  private secrets: string[] = [];
  /** Rooms we already told to disconnect. */
  private closed = new WeakSet<MediaRoom>();
  /** The ref of the outstanding media_grant request: only a reply or refusal echoing it is ours to use. */
  private asked: string | null = null;
  /** Refusals and failed connects since the last successful connect: the index into MEDIA_GRANT_BACKOFF_MS. */
  private failures = 0;
  private retryTimer: TimerId | undefined;
  /** Runs out MEDIA_GRANT_REPLY_TIMEOUT_MS after a request: no reply by then counts as a refusal. */
  private replyTimer: TimerId | undefined;
  /** Bumped by every change a publish depends on (mute, permission, track): a failed publish is tried again only
   * when something changed while it was out, never in a loop on its own failure. */
  private changes = 0;

  constructor(private readonly opts: PublisherOptions) {}

  /** A grant from joined (a new seat: any old connection is for the old seat), resumed (the same seat) or the reply
   * to our own media_grant request (the same seat; null: media is off on this server), with the ref it echoed. */
  grant(grant: LiveKitGrant | null, reply: "joined" | "resumed" | "media_grant", ref: string | null = null): void {
    if (grant) this.secrets = [...this.secrets.slice(-7), grant.token];
    if (reply === "joined") {
      this.stop();
      this.fresh = grant;
    } else {
      if (reply === "media_grant") {
        if (!this.asked || ref !== this.asked) {
          // not the outstanding request (a late reply, one after leave, one with no ref): not used; no body logged
          this.log("media grant reply ignored: not the outstanding request");
          return;
        }
      }
      this.answered(); // on resumed, a request on the old socket is gone with it
      if (this.state === "down") {
        this.clearRetry(); // this grant (or "media is off") supersedes a pending retry
        this.fresh = grant; // a fresh token for a connection lost with the network
      } // resumed while up or connecting: keep the connection, no reconnect
    }
    this.pump();
  }

  /** The server refused our media_grant request for now (media_rate_limited): ask again after the backoff. */
  grantRefused(ref: string | null): void {
    if (!this.asked || ref !== this.asked) {
      this.log("media grant refusal ignored: not the outstanding request");
      return;
    }
    this.answered();
    this.retryLater("media grant refused for now");
  }

  /** The microphone track (null: closed, denied or no device). */
  setTrack(track: MediaStreamTrack | null): void {
    if (track === this.track) return;
    const room = this.room;
    if (this.published && room) {
      this.published = false;
      void room.unpublish().catch((e) => this.fail("unpublish failed", e));
    }
    this.track = track;
    this.changes += 1;
    this.pump();
  }

  /** OUR protocol's word on this seat's mute (the person's tap, and seat_updated). */
  setMuted(muted: boolean): void {
    if (muted !== this.muted) this.changes += 1;
    this.muted = muted;
    this.applyMute();
    this.pump(); // unmuted after the server took the track down: publish again if the permission is back
  }

  /** Leave, or the page going away: DISCONNECT, and nothing else. The publisher can be used again by a new joined.
   *
   * MAJLIS-53. This used to `await room.unpublish()` and THEN `await room.disconnect()`, inside an un-awaited async
   * IIFE: two network round trips, the second one behind the first. On `pagehide` (App.tsx) the page may be frozen
   * before the second ever goes out - and the second is the one that matters. The first buys nothing: a bare
   * disconnect already ends the seat's feeds by sid, with their reasons, because the server's ingest listens for
   * `participant_disconnected` (server/majlis_server/media/ingest.py, MAJLIS-52 - there is a comment at that
   * listener saying this module depends on it).
   *
   * Every stop path, not a pagehide special case: `stop()` has five callers - leave, the seat gone, pagehide, the
   * effect cleanup, and a room replaced by a new joined - and a parameter for one of them would leave the other
   * four paying for a round trip that buys nothing.
   *
   * The microphone track is NOT stopped by this: `disconnect(false)` keeps it alive exactly as
   * `unpublishTrack(t, false)` did, because mic.ts owns the track and the mic ring reads it too (livekitRoom.ts
   * says so; livekitRoom.test.ts pins both flags and the survival).
   *
   * WHAT THIS DOES NOT DO: it makes a ghost participant RARER, NOT IMPOSSIBLE. One round trip is likelier to
   * complete than two, but a page frozen fast enough still sends nothing at all. MAJLIS-21 - the server removing a
   * departed participant - remains the guarantee, and this is not a replacement for it. */
  stop(): void {
    this.clearRetry();
    this.answered();
    this.failures = 0;
    this.fresh = null;
    const room = this.room;
    this.room = null;
    this.state = "down";
    this.published = false;
    this.publishing = false;
    this.opts.hearing?.clear(); // MAJLIS-62: no voice outlives the connection it came on
    this.opts.voiceTrack?.attach(null); // MAJLIS-140: nor does its voice track
    if (!room) return;
    this.closed.add(room);
    void room.disconnect().catch((e) => this.fail("disconnect failed", e));
  }

  // ---------------------------------------------------------------- internals

  private pump(): void {
    if (this.state === "down" && this.fresh) return this.connect();
    const room = this.room;
    if (this.state !== "up" || !room || !this.track || this.published || this.publishing || this.muted) return;
    // MAJLIS-61 trap 3: never publish without the permission NOW (a publish racing a revoke hangs ~10 s).
    if (!room.canPublish()) return;
    if (this.track.readyState === "ended") {
      // mic.ts owns the capture and it has ended: publishing it would fail, and a second capture is not ours to open
      this.log("microphone track ended; not published");
      return;
    }
    this.publish();
  }

  private connect(): void {
    const g = this.fresh!;
    this.fresh = null; // used once, never again
    this.state = "connecting";
    const room: MediaRoom = this.opts.factory({
      onUnpublished: () => {
        if (this.room !== room || !this.published) return;
        // the server took the track down (our mute, enforced): the next publication starts unmuted
        this.published = false;
        this.roomMuted = false;
        this.log("media track taken down by the server");
        this.pump();
      },
      onPermissionsChanged: () => {
        if (this.room !== room) return;
        this.changes += 1;
        this.log("media publish permission changed", { canPublish: room.canPublish() });
        this.pump();
      },
      onRemoteAudio: (key, track) => {
        if (this.room === room) this.opts.hearing?.add(key, track);
      },
      onRemoteAudioGone: (key) => {
        if (this.room === room) this.opts.hearing?.remove(key);
      },
      onVoiceTrack: (e) => {
        if (this.room === room) this.opts.voiceTrack?.event(e);
      },
      onDisconnected: () => {
        if (this.room !== room) return;
        this.room = null;
        this.state = "down";
        this.published = false;
        this.publishing = false;
        this.opts.hearing?.clear();
        this.opts.voiceTrack?.attach(null);
        this.log("media link lost; waiting for a fresh grant");
        this.ask();
      },
    });
    this.room = room;
    // MAJLIS-140: before connect - the voice track's own events may arrive while it is connecting
    this.opts.voiceTrack?.attach({ setVoiceSubscribed: (want) => room.setVoiceSubscribed?.(want) });
    room.connect(g.url, g.token).then(
      () => {
        if (this.room !== room) {
          // replaced or stopped while connecting: make sure this late connection does not stay up
          if (!this.closed.has(room)) {
            this.closed.add(room);
            void room.disconnect().catch(() => undefined);
          }
          return;
        }
        this.state = "up";
        this.failures = 0; // reconnected: the next loss asks at once, the next refusal waits the first step again
        this.clearRetry();
        this.pump();
      },
      (e) => {
        if (this.room !== room) return;
        this.room = null;
        this.state = "down";
        this.opts.hearing?.clear();
        this.opts.voiceTrack?.attach(null);
        this.fail("media connect failed", e);
        this.pump();
        if (this.state === "down") this.retryLater("media connect failed; asking for another grant later");
      },
    );
  }

  private publish(): void {
    const room = this.room!;
    const track = this.track!;
    const started = this.changes;
    this.publishing = true;
    room.publish(track, "microphone").then(
      () => {
        if (this.room !== room) return;
        this.publishing = false;
        if (this.track !== track) {
          // the microphone changed while publishing: take the old track down and publish the current one
          void room.unpublish().catch((e) => this.fail("unpublish failed", e));
          return this.pump();
        }
        this.published = true;
        this.roomMuted = false;
        this.applyMute(); // muted again while this was out: the last word wins, it is muted at once
        // EM-25: a new publication must not leave track.enabled other than the gate computed it
        this.opts.reassertMic?.();
      },
      (e) => {
        if (this.room !== room) return;
        this.publishing = false;
        this.fail("media publish failed", e);
        // a publish that raced a revoke rejects late (~10 s): if the mute or the permission changed meanwhile, the
        // current state decides; if nothing changed, do not loop on the same failure
        if (this.changes !== started) this.pump();
      },
    );
  }

  /** Send one media_grant request, if the connection is down and no grant or request is already in hand. */
  private ask(): void {
    this.clearRetry();
    const { requestGrant, clock } = this.opts;
    if (!requestGrant || !clock || this.state !== "down" || this.fresh || this.asked) return;
    this.asked = requestGrant(); // null: the socket is not live; the resume brings a fresh grant
    if (!this.asked) return;
    this.log("asking for a fresh media grant");
    this.replyTimer = clock.setTimeout(() => {
      this.replyTimer = undefined;
      if (!this.asked) return;
      this.asked = null; // a reply after this is no longer the outstanding request: not used
      this.retryLater("no media grant reply in time");
    }, MEDIA_GRANT_REPLY_TIMEOUT_MS);
  }

  /** The outstanding request is over (answered, refused, or gone with its socket). */
  private answered(): void {
    this.asked = null;
    if (this.replyTimer !== undefined) this.opts.clock?.clearTimeout(this.replyTimer);
    this.replyTimer = undefined;
  }

  private retryLater(why: string): void {
    const { requestGrant, clock } = this.opts;
    if (!requestGrant || !clock) return;
    this.clearRetry();
    const delay = MEDIA_GRANT_BACKOFF_MS[Math.min(this.failures, MEDIA_GRANT_BACKOFF_MS.length - 1)];
    this.failures += 1;
    this.log(why, { retryInMs: delay });
    this.retryTimer = clock.setTimeout(() => {
      this.retryTimer = undefined;
      this.ask();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== undefined) this.opts.clock?.clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }

  private applyMute(): void {
    const room = this.room;
    if (!room || !this.published || this.roomMuted === this.muted) return;
    this.roomMuted = this.muted;
    const call = this.muted ? room.mute() : room.unmute();
    call.catch((e) => this.fail(this.muted ? "media mute failed" : "media unmute failed", e));
    // EM-25: whichever way that call goes, livekit-client has written track.enabled by the time it settles.
    // The gate writes the flag it computed afterwards, so an unmute cannot open a microphone the gate holds shut.
    void call.then(
      () => this.opts.reassertMic?.(),
      () => this.opts.reassertMic?.(),
    );
  }

  private fail(message: string, e: unknown): void {
    this.log(message, { error: redact(e, this.secrets) });
  }

  private log(message: string, body?: unknown): void {
    this.opts.log?.(message, body);
  }
}
